'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["count", "until"],
  _excluded2 = ["byMonthDay"],
  _excluded3 = ["byDay"];
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Checkbox, { checkboxClasses } from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel, { formControlLabelClasses } from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useSchedulerStoreContext } from '@mui/x-scheduler-headless/use-scheduler-store-context';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { schedulerEventSelectors, schedulerRecurringEventSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import { useEventDialogStyledContext } from "./EventDialogStyledContext.mjs";
import { getEndsSelectionFromRRule } from "./utils.mjs";
import { formatDayOfMonthAndMonthFullLetter } from "../../utils/date-utils.mjs";
import { EventDialogTabPanel, EventDialogTabContent } from "./EventDialogTabPanel.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SectionHeaderTitle = styled(Typography, {
  name: 'MuiEventDialog',
  slot: 'SectionHeaderTitle'
})(({
  theme
}) => ({
  textTransform: 'uppercase',
  color: (theme.vars || theme).palette.text.secondary
}));
const RecurrenceSelectorContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'RecurrenceSelectorContainer'
})(({
  theme
}) => ({
  display: 'inline-flex',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  width: 'fit-content',
  maxWidth: '100%'
}));
const RadioButtonLabel = styled(FormControlLabel, {
  name: 'MuiEventDialog',
  slot: 'RadioButtonLabel'
})(({
  theme
}) => ({
  color: (theme.vars || theme).palette.text.primary,
  [`& .${formControlLabelClasses.label}`]: {
    minWidth: 60
  }
}));
const RepeatSectionLabel = styled(FormLabel, {
  name: 'MuiEventDialog',
  slot: 'RepeatSectionLabel'
})(({
  theme
}) => ({
  color: (theme.vars || theme).palette.text.primary,
  minWidth: 60
}));
const EndsRadioGroup = styled(RadioGroup, {
  name: 'MuiEventDialog',
  slot: 'EndsRadioGroup'
})({
  display: 'flex',
  flexDirection: 'column',
  gap: 8
});
const RepeatSectionFieldset = styled('fieldset', {
  name: 'MuiEventDialog',
  slot: 'RepeatSectionFieldset'
})({
  border: 0,
  margin: 0,
  padding: 0
});
const RepeatSectionContent = styled('div', {
  name: 'MuiEventDialog',
  slot: 'RepeatSectionContent'
})({
  display: 'flex',
  flexDirection: 'column',
  gap: 12
});
const InlineRow = styled('div', {
  name: 'MuiEventDialog',
  slot: 'InlineRow'
})({
  display: 'flex',
  alignItems: 'center',
  gap: 8
});
const RecurrenceSelectorToggleGroup = styled(ToggleButtonGroup, {
  name: 'MuiEventDialog',
  slot: 'RecurrenceSelectorToggleGroup'
})(({
  theme
}) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    minWidth: 0,
    display: 'block',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0
    }
  },
  [`& .${toggleButtonGroupClasses.middleButton}, & .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: '1px solid transparent'
  }
}));
const WeekDaySelectorCheckbox = styled(Checkbox, {
  name: 'MuiEventDialog',
  slot: 'WeekDaySelectorCheckbox'
})(({
  theme
}) => _extends({}, theme.typography.button, {
  fontSize: theme.typography.pxToRem(13),
  padding: theme.spacing(0.75),
  margin: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.action.active,
  [`&.${checkboxClasses.checked}`]: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.selected
  }
}));
const FrequencySelect = styled(Select, {
  name: 'MuiEventDialog',
  slot: 'FrequencySelect'
})({
  maxWidth: 120
});
const SmallNumberField = styled(TextField, {
  name: 'MuiEventDialog',
  slot: 'SmallNumberField'
})({
  maxWidth: 100
});
export function RecurrenceTab(props) {
  const {
    occurrence,
    controlled,
    setControlled,
    value: tabValue
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const {
    classes,
    localeText
  } = useEventDialogStyledContext();
  const store = useSchedulerStoreContext();

  // Selector hooks
  const isPropertyReadOnly = useStore(store, schedulerEventSelectors.isPropertyReadOnly, occurrence.id);
  const customDisabled = controlled.recurrenceSelection !== 'custom' || isPropertyReadOnly('rrule');
  const monthlyRef = useStore(store, schedulerRecurringEventSelectors.monthlyReference, occurrence.displayTimezone.start);
  const weeklyDays = useStore(store, schedulerRecurringEventSelectors.weeklyDays);
  const handleRecurrenceSelectionChange = newSelection => {
    if (newSelection === 'custom') {
      const base = occurrence.displayTimezone.rrule;
      setControlled(prev => _extends({}, prev, {
        recurrenceSelection: 'custom',
        rruleDraft: _extends({
          freq: base?.freq ?? prev.rruleDraft.freq ?? 'WEEKLY',
          interval: base?.interval ?? prev.rruleDraft.interval ?? 1,
          byDay: base?.byDay ?? prev.rruleDraft.byDay ?? [],
          byMonthDay: base?.byMonthDay ?? prev.rruleDraft.byMonthDay ?? []
        }, base?.count ? {
          count: base.count
        } : {}, base?.until ? {
          until: base.until
        } : {})
      }));
    } else {
      setControlled(prev => _extends({}, prev, {
        recurrenceSelection: newSelection,
        rruleDraft: {
          freq: 'WEEKLY',
          interval: 1,
          byDay: [],
          byMonthDay: []
        }
      }));
    }
  };
  const handleChangeInterval = event => {
    const intervalValue = Number(event.currentTarget.value || 1);
    setControlled(prev => _extends({}, prev, {
      rruleDraft: _extends({}, prev.rruleDraft, {
        interval: intervalValue
      })
    }));
  };
  const handleChangeFrequency = newFrequency => {
    if (newFrequency == null) {
      return;
    }
    setControlled(prev => {
      // When switching frequency, clear byDay/byMonthDay to avoid stale values
      // from a different frequency leaking (e.g. monthly ordinal "2TU" into weekly)
      return _extends({}, prev, {
        rruleDraft: _extends({}, prev.rruleDraft, {
          freq: newFrequency,
          byDay: [],
          byMonthDay: newFrequency === 'MONTHLY' ? [monthlyRef.dayOfMonth] : []
        })
      });
    });
  };
  const handleEndsChange = endsSelection => {
    switch (endsSelection) {
      case 'until':
        {
          setControlled(prev => _extends({}, prev, {
            rruleDraft: _extends({}, prev.rruleDraft, {
              until: adapter.date(prev.endDate, 'default'),
              count: undefined
            })
          }));
          break;
        }
      case 'after':
        {
          setControlled(prev => _extends({}, prev, {
            rruleDraft: _extends({}, prev.rruleDraft, {
              count: 1,
              until: undefined
            })
          }));
          break;
        }
      case 'never':
      default:
        {
          setControlled(prev => {
            const _prev$rruleDraft = prev.rruleDraft,
              rest = _objectWithoutPropertiesLoose(_prev$rruleDraft, _excluded);
            return _extends({}, prev, {
              rruleDraft: rest
            });
          });
          break;
        }
    }
  };
  const handleChangeCount = event => {
    const countValue = Number(event.currentTarget.value || 1);
    setControlled(prev => _extends({}, prev, {
      rruleDraft: _extends({}, prev.rruleDraft, {
        count: countValue
      })
    }));
  };
  const handleChangeUntil = event => {
    const untilValue = event.currentTarget.value;
    setControlled(prev => _extends({}, prev, {
      rruleDraft: _extends({}, prev.rruleDraft, {
        until: adapter.date(untilValue, 'default')
      })
    }));
  };
  const handleChangeWeeklyDays = dayCode => {
    const byDay = controlled.rruleDraft.byDay ?? [];
    const next = byDay.includes(dayCode) ? byDay.filter(d => d !== dayCode) : [...byDay, dayCode];
    setControlled(prev => _extends({}, prev, {
      rruleDraft: _extends({}, prev.rruleDraft, {
        byDay: next
      })
    }));
  };
  const handleChangeMonthlyGroup = next => {
    const nextKey = next[0];
    setControlled(prev => {
      if (nextKey === 'byDay') {
        const byDayValue = `${monthlyRef.ord}${monthlyRef.code}`;
        const _prev$rruleDraft2 = prev.rruleDraft,
          rest = _objectWithoutPropertiesLoose(_prev$rruleDraft2, _excluded2);
        return _extends({}, prev, {
          rruleDraft: _extends({}, rest, {
            byDay: [byDayValue]
          })
        });
      }
      const _prev$rruleDraft3 = prev.rruleDraft,
        rest = _objectWithoutPropertiesLoose(_prev$rruleDraft3, _excluded3);
      return _extends({}, prev, {
        rruleDraft: _extends({}, rest, {
          byMonthDay: [monthlyRef.dayOfMonth]
        })
      });
    });
  };
  const customEndsValue = getEndsSelectionFromRRule(controlled.rruleDraft);
  const weekday = adapter.format(occurrence.displayTimezone.start.value, 'weekday');
  const dateForYearlyOption = formatDayOfMonthAndMonthFullLetter(occurrence.displayTimezone.start.value, adapter);
  const recurrenceOptions = [{
    label: `${localeText.recurrenceNoRepeat}`,
    value: null
  }, {
    label: `${localeText.recurrenceDailyPresetLabel}`,
    value: 'DAILY'
  }, {
    label: `${localeText.recurrenceWeeklyPresetLabel(weekday)}`,
    value: 'WEEKLY'
  }, {
    label: `${localeText.recurrenceMonthlyPresetLabel(adapter.getDate(occurrence.displayTimezone.start.value))}`,
    value: 'MONTHLY'
  }, {
    label: `${localeText.recurrenceYearlyPresetLabel(dateForYearlyOption)}`,
    value: 'YEARLY'
  }, {
    label: `${localeText.recurrenceCustomRepeat}`,
    value: 'custom'
  }];
  const recurrenceFrequencyOptions = [{
    label: `${localeText.recurrenceDailyFrequencyLabel}`,
    value: 'DAILY'
  }, {
    label: `${localeText.recurrenceWeeklyFrequencyLabel}`,
    value: 'WEEKLY'
  }, {
    label: `${localeText.recurrenceMonthlyFrequencyLabel}`,
    value: 'MONTHLY'
  }, {
    label: `${localeText.recurrenceYearlyFrequencyLabel}`,
    value: 'YEARLY'
  }];
  const weeklyDayItems = React.useMemo(() => weeklyDays.map(({
    code,
    date
  }) => ({
    value: code,
    ariaLabel: adapter.format(date, 'weekday'),
    label: adapter.format(date, 'weekday3Letters')
  })), [adapter, weeklyDays]);
  const monthlyItems = React.useMemo(() => {
    const ordinal = monthlyRef.ord;
    const dayOfMonthLabel = localeText.recurrenceMonthlyDayOfMonthLabel?.(monthlyRef.dayOfMonth);
    const isLast = ordinal === -1;
    const weekdayShort = adapter.formatByString(monthlyRef.date, 'ccc');
    const weekAriaLabel = isLast ? localeText.recurrenceMonthlyLastWeekAriaLabel(weekday) : localeText.recurrenceMonthlyWeekNumberAriaLabel?.(ordinal, weekday);
    const weekLabel = isLast ? localeText.recurrenceMonthlyLastWeekLabel(weekdayShort) : localeText.recurrenceMonthlyWeekNumberLabel?.(ordinal, weekdayShort);
    return [{
      value: 'byMonthDay',
      ariaLabel: dayOfMonthLabel,
      label: dayOfMonthLabel
    }, {
      value: 'byDay',
      ariaLabel: weekAriaLabel,
      label: weekLabel
    }];
  }, [adapter, monthlyRef.date, monthlyRef.dayOfMonth, monthlyRef.ord, localeText, weekday]);
  const monthlyMode = controlled.rruleDraft.byDay?.length ? 'byDay' : 'byMonthDay';
  return /*#__PURE__*/_jsx(EventDialogTabPanel, {
    role: "tabpanel",
    id: "recurrence-tabpanel",
    "aria-labelledby": "recurrence-tab",
    className: classes.eventDialogTabPanel,
    hidden: tabValue !== 'recurrence',
    children: /*#__PURE__*/_jsxs(EventDialogTabContent, {
      className: classes.eventDialogTabContent,
      children: [/*#__PURE__*/_jsxs(FormControl, {
        fullWidth: true,
        size: "small",
        children: [/*#__PURE__*/_jsx(InputLabel, {
          id: "recurrence-preset-label",
          children: localeText.recurrenceMainSelectCustomLabel
        }), /*#__PURE__*/_jsx(Select, {
          labelId: "recurrence-preset-label",
          name: "recurrencePreset",
          label: localeText.recurrenceMainSelectCustomLabel,
          value: controlled.recurrenceSelection ?? 'no-repeat',
          onChange: event => {
            const value = event.target.value;
            handleRecurrenceSelectionChange(value === 'no-repeat' ? null : value);
          },
          readOnly: isPropertyReadOnly('rrule'),
          "aria-label": localeText.recurrenceLabel,
          children: recurrenceOptions.map(({
            label,
            value: optionValue
          }) => /*#__PURE__*/_jsx(MenuItem, {
            value: optionValue ?? 'no-repeat',
            children: label
          }, label))
        })]
      }), /*#__PURE__*/_jsxs(RepeatSectionFieldset, {
        "aria-label": localeText.recurrenceRepeatLabel,
        className: classes.eventDialogRepeatSectionFieldset,
        children: [/*#__PURE__*/_jsx(SectionHeaderTitle, {
          variant: "subtitle2",
          className: classes.eventDialogSectionHeaderTitle,
          children: localeText.recurrenceRepeatLabel
        }), /*#__PURE__*/_jsxs(RepeatSectionContent, {
          className: classes.eventDialogRepeatSectionContent,
          children: [/*#__PURE__*/_jsxs(InlineRow, {
            className: classes.eventDialogInlineRow,
            children: [/*#__PURE__*/_jsx(RepeatSectionLabel, {
              className: classes.eventDialogRepeatSectionLabel,
              children: localeText.recurrenceEveryLabel
            }), /*#__PURE__*/_jsx(SmallNumberField, {
              className: classes.eventDialogSmallNumberField,
              type: "number",
              slotProps: {
                htmlInput: {
                  min: 1
                }
              },
              value: controlled.rruleDraft.interval,
              onChange: handleChangeInterval,
              disabled: customDisabled,
              size: "small"
            }), /*#__PURE__*/_jsx(FrequencySelect, {
              className: classes.eventDialogFrequencySelect,
              value: controlled.rruleDraft.freq,
              onChange: event => handleChangeFrequency(event.target.value),
              disabled: customDisabled,
              size: "small",
              fullWidth: true,
              children: recurrenceFrequencyOptions.map(({
                label,
                value: freqValue
              }) => /*#__PURE__*/_jsx(MenuItem, {
                value: freqValue,
                children: label
              }, label))
            })]
          }), controlled.rruleDraft.freq === 'WEEKLY' && /*#__PURE__*/_jsxs(InlineRow, {
            className: classes.eventDialogInlineRow,
            children: [/*#__PURE__*/_jsx(RepeatSectionLabel, {
              className: classes.eventDialogRepeatSectionLabel,
              children: localeText.recurrenceWeeklyMonthlySpecificInputsLabel
            }), /*#__PURE__*/_jsx(RecurrenceSelectorContainer, {
              className: classes.eventDialogRecurrenceSelectorContainer,
              role: "group",
              "aria-label": localeText.recurrenceWeeklyMonthlySpecificInputsLabel,
              children: weeklyDayItems.map(({
                value: dayValue,
                ariaLabel,
                label
              }) => /*#__PURE__*/_jsx(WeekDaySelectorCheckbox, {
                className: classes.eventDialogWeekDaySelectorCheckbox,
                icon: /*#__PURE__*/_jsx("span", {
                  children: label
                }),
                checkedIcon: /*#__PURE__*/_jsx("span", {
                  children: label
                }),
                checked: controlled.rruleDraft.byDay?.includes(dayValue) ?? false,
                disabled: customDisabled,
                onChange: () => handleChangeWeeklyDays(dayValue),
                slotProps: {
                  input: {
                    'aria-label': ariaLabel
                  }
                }
              }, dayValue))
            })]
          }), controlled.rruleDraft.freq === 'MONTHLY' && /*#__PURE__*/_jsxs(InlineRow, {
            className: classes.eventDialogInlineRow,
            children: [/*#__PURE__*/_jsx(RepeatSectionLabel, {
              className: classes.eventDialogRepeatSectionLabel,
              children: localeText.recurrenceWeeklyMonthlySpecificInputsLabel
            }), /*#__PURE__*/_jsx(RecurrenceSelectorContainer, {
              className: classes.eventDialogRecurrenceSelectorContainer,
              children: /*#__PURE__*/_jsx(RecurrenceSelectorToggleGroup, {
                className: classes.eventDialogRecurrenceSelectorToggleGroup,
                size: "small",
                value: monthlyMode,
                exclusive: true,
                onChange: (_, newValue) => {
                  if (newValue) {
                    handleChangeMonthlyGroup([newValue]);
                  }
                },
                disabled: customDisabled,
                "aria-label": localeText.recurrenceWeeklyMonthlySpecificInputsLabel,
                children: monthlyItems.map(({
                  value: monthlyValue,
                  ariaLabel,
                  label
                }) => /*#__PURE__*/_jsx(ToggleButton, {
                  "aria-label": ariaLabel,
                  value: monthlyValue,
                  children: label
                }, monthlyValue))
              })
            })]
          })]
        })]
      }), /*#__PURE__*/_jsxs(FormControl, {
        component: "fieldset",
        "aria-label": localeText.recurrenceEndsLabel,
        children: [/*#__PURE__*/_jsx(SectionHeaderTitle, {
          variant: "subtitle2",
          className: classes.eventDialogSectionHeaderTitle,
          children: localeText.recurrenceEndsLabel
        }), /*#__PURE__*/_jsxs(EndsRadioGroup, {
          className: classes.eventDialogEndsRadioGroup,
          value: customEndsValue,
          onChange: event => handleEndsChange(event.target.value),
          children: [/*#__PURE__*/_jsx(RadioButtonLabel, {
            className: classes.eventDialogRadioButtonLabel,
            value: "never",
            control: /*#__PURE__*/_jsx(Radio, {
              size: "small",
              disabled: customDisabled
            }),
            label: localeText.recurrenceEndsNeverLabel
          }), /*#__PURE__*/_jsxs(InlineRow, {
            className: classes.eventDialogInlineRow,
            children: [/*#__PURE__*/_jsx(RadioButtonLabel, {
              className: classes.eventDialogRadioButtonLabel,
              value: "after",
              control: /*#__PURE__*/_jsx(Radio, {
                size: "small",
                disabled: customDisabled
              }),
              label: localeText.recurrenceEndsAfterLabel
            }), /*#__PURE__*/_jsx(SmallNumberField, {
              className: classes.eventDialogSmallNumberField,
              type: "number",
              slotProps: {
                htmlInput: {
                  min: 1
                }
              },
              value: customEndsValue === 'after' ? controlled.rruleDraft.count ?? 1 : 1,
              onChange: handleChangeCount,
              disabled: customDisabled || customEndsValue !== 'after',
              size: "small"
            }), localeText.recurrenceEndsTimesLabel]
          }), /*#__PURE__*/_jsxs(InlineRow, {
            className: classes.eventDialogInlineRow,
            onClick: () => {
              if (!customDisabled && customEndsValue !== 'until') {
                handleEndsChange('until');
              }
            },
            children: [/*#__PURE__*/_jsx(RadioButtonLabel, {
              className: classes.eventDialogRadioButtonLabel,
              value: "until",
              control: /*#__PURE__*/_jsx(Radio, {
                size: "small",
                disabled: customDisabled
              }),
              label: localeText.recurrenceEndsUntilLabel
            }), /*#__PURE__*/_jsx(TextField, {
              type: "date",
              value: customEndsValue === 'until' && adapter.isValid(controlled.rruleDraft.until ?? null) ? adapter.formatByString(controlled.rruleDraft.until, 'yyyy-MM-dd') : '',
              onChange: handleChangeUntil,
              disabled: customDisabled || customEndsValue !== 'until',
              size: "small",
              slotProps: {
                inputLabel: {
                  shrink: true
                }
              }
            })]
          })]
        })]
      })]
    })
  });
}